"use client";

import React from "react";
import { ComponentConfig } from "@puckeditor/core";
import Link from "next/link";
import { businessData } from "@/data/business";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface HighlightItem {
  icon: string;
  title: string;
  subtitle: string;
}

export interface StatItem {
  value: number;
  suffix: string;
  label: string;
}

export interface ShaairaHeroProps {
  headline?: string;
  subheadline?: string;
  videoSrc?: string;
  secondaryVideoSrc?: string;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  highlights?: HighlightItem[];
  stats?: StatItem[];
}

export const ShaairaHeroBlock: ComponentConfig<ShaairaHeroProps> = {
  label: "Shaaira Hero Section",
  fields: {
    headline: { type: "text", label: "Headline Title" },
    subheadline: { type: "textarea", label: "Lead Subheadline" },
    videoSrc: { type: "text", label: "Hero Video Asset URL" },
    secondaryVideoSrc: { type: "text", label: "Mobile Hero Video Asset URL" },
    primaryCtaText: { type: "text", label: "Primary CTA Text" },
    primaryCtaLink: { type: "text", label: "Primary CTA Link" },
    secondaryCtaText: { type: "text", label: "Secondary CTA Text" },
    secondaryCtaLink: { type: "text", label: "Secondary CTA Link" },
    highlights: {
      type: "array",
      getItemSummary: (item: any) => item.title || "Highlight",
      arrayFields: {
        icon: {
          type: "select",
          label: "Highlight Icon",
          options: [
            { label: "Quality shield", value: "shield" },
            { label: "MOQ / box", value: "box" },
            { label: "Delivery truck", value: "truck" },
          ],
        },
        title: { type: "text", label: "Title" },
        subtitle: { type: "text", label: "Subtitle" },
      },
      defaultItemProps: {
        icon: "shield",
        title: "Analytical QC",
        subtitle: "CoA with every approved batch",
      },
    },
    stats: {
      type: "array",
      getItemSummary: (item: any) => `${item.value}${item.suffix} ${item.label}`,
      arrayFields: {
        value: { type: "number", label: "Value" },
        suffix: { type: "text", label: "Suffix (+, %, units, days)" },
        label: { type: "text", label: "Label" },
      },
      defaultItemProps: {
        value: 500,
        suffix: " units",
        label: "Starting MOQ",
      },
    },
  },
  defaultProps: {
    headline: "Premium Cosmeceutical Manufacturing.",
    subheadline: "End-to-end custom formulation and private-label production with specification-led development, packaging support, and selected starting MOQs from 500–1000 pc.",
    videoSrc: "/assets/hero-cosmetics-loop.mp4",
    secondaryVideoSrc: "/assets/hero-cosmetics-loop-2.mp4",
    primaryCtaText: "Samples",
    primaryCtaLink: "/contact",
    secondaryCtaText: "Products",
    secondaryCtaLink: "/product-range",
    highlights: businessData.heroHighlights,
    stats: businessData.stats,
  },
  render: function ShaairaHero({
    headline,
    subheadline,
    videoSrc,
    secondaryVideoSrc,
    primaryCtaText,
    primaryCtaLink,
    secondaryCtaText,
    secondaryCtaLink,
    highlights,
    stats,
  }) {
    return (
      <section className="hero">
        <div className="container hero-grid">
          <div className="hero-copy" data-animate>
            <h1>{headline}</h1>
            <p className="lead">{subheadline}</p>
            <div className="hero-cta desktop-cta">
              <Link className="btn btn-primary" href={primaryCtaLink || "/contact"}>
                {primaryCtaText}{" "}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <Link className="btn btn-ghost" href={secondaryCtaLink || "/product-range"}>
                {secondaryCtaText}
              </Link>
            </div>
          </div>

          <div className="hero-media" data-animate>
            <div className="hero-spec-card hero-video-card" style={{ position: "relative", overflow: "hidden" }}>
              <video
                autoPlay
                muted
                playsInline
                loop
                preload="metadata"
                poster="/og-lab-v2.jpg"
                className="hero-video"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              >
                <source media="(max-width: 640px)" src={resolveMediaUrl(secondaryVideoSrc, "/assets/hero-cosmetics-loop-2.mp4")} type="video/mp4" />
                <source src={resolveMediaUrl(videoSrc, "/assets/hero-cosmetics-loop.mp4")} type="video/mp4" />
              </video>
            </div>
          </div>

          <div className="hero-cta mobile-cta" data-animate>
            <Link className="btn btn-primary" href={primaryCtaLink || "/contact"}>
              {primaryCtaText}{" "}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <Link className="btn btn-ghost" href={secondaryCtaLink || "/product-range"}>
              {secondaryCtaText}
            </Link>
          </div>
        </div>

        <div className="container" data-animate>
          <div className="hero-highlights">
            {(highlights || []).map((hh, idx) => (
              <div className="hh-item" key={idx}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {hh.icon === "box" ? (
                    <>
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                      <line x1="12" y1="22.08" x2="12" y2="12" />
                    </>
                  ) : hh.icon === "truck" ? (
                    <>
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </>
                  ) : (
                    <>
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <polyline points="9 12 11 14 15 10" />
                    </>
                  )}
                </svg>
                <div>
                  <strong>{hh.title}</strong>
                  <span>{hh.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-stats container">
          {(stats || []).map((stat, idx) => (
            <div className="stat" data-animate key={idx}>
              <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>
    );
  },
};
